import { useState, useRef, useEffect } from "react";
import Button from "../../atoms/Button/Button";
import emailjs from "@emailjs/browser";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

interface FormData {
  name: string;
  email: string;
  message: string;
  [key: string]: string;
}

interface SubmitStatus {
  success?: boolean;
  message?: string;
}

const ContactForm = () => {
  const form = useRef<HTMLFormElement>(null);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({});

  const handleOpenPdf = (pdfName: string) => {
    const pdfUrl = `${process.env.PUBLIC_URL}/static/${pdfName}.pdf`;
    window.open(pdfUrl, "_blank");
  };

  // emailjs initialization when component mounts
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "";
    try {
      emailjs.init(publicKey);
    } catch (error) {
      console.error("EmailJS initialization failed:", error);
    }
  }, []);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.current) return;

    if (!executeRecaptcha) {
      setSubmitStatus({
        success: false,
        message: "reCAPTCHA nie je ešte pripravený. Prosím, skúste znova.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({});

    try {
      // reCAPTCHA v3
      const captchaToken = await executeRecaptcha("contact_form_submit");

      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";

      const formDataWithCaptcha = {
        ...formData,
        "g-recaptcha-response": captchaToken,
      };

      await emailjs.send(serviceId, templateId, formDataWithCaptcha);

      setSubmitStatus({
        success: true,
        message: "Ďakujeme! Váš e-mail bol úspešne odoslaný.",
      });

      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("Email sending failed:", error);
      setSubmitStatus({
        success: false,
        message:
          "Nastala chyba pri odosielaní. Prosím, skúste to znovu neskôr.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dui-contact-form-wrapper" id="contact">
      <h2 className="dui-contact-form-heading">Kontaktujte nás</h2>
      <div className="dui-contact-form">
        <form
          ref={form}
          onSubmit={handleSubmit}
          className="dui-contact-form-section"
        >
          <label htmlFor="name">Meno a priezvisko</label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Napíšte text..."
            required
          />

          <label htmlFor="email">E-mail</label>
          <input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Napíšte text..."
            required
          />

          <label htmlFor="message">Správa pre nás</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Napíšte text..."
            required
          ></textarea>

          {submitStatus.message && (
            <div
              className={`dui-contact-form-status ${
                submitStatus.success ? "success" : "error"
              }`}
            >
              {submitStatus.message}
            </div>
          )}

          <Button
            className="dui-contact-form-button"
            corners="rounded"
            disabled={isSubmitting}
            onClick={() => form.current?.requestSubmit()}
          >
            {isSubmitting ? "Odosielam..." : "Poslať formulár"}
          </Button>
        </form>

        <div className="dui-contact-form-divider" />

        <div className="dui-contact-form-info">
          <ul>
            <li>
              <strong
                onClick={() => handleOpenPdf("GDPR_doktorui")}
                style={{ cursor: "pointer" }}
              >
                Ochrana osobných údajov
              </strong>{" "}
              <span
                onClick={() => handleOpenPdf("GDPR_doktorui_CZ")}
                style={{ cursor: "pointer" }}
              >
                (verzia CZ)
              </span>
            </li>
            <li>
              <strong
                onClick={() => handleOpenPdf("DoktorUI_VOP")}
                style={{ cursor: "pointer" }}
              >
                Obchodné podmienky
              </strong>{" "}
              <span
                onClick={() => handleOpenPdf("DoktorUI_VOP_CZ")}
                style={{ cursor: "pointer" }}
              >
                (verzia CZ)
              </span>
            </li>
            <li>
              <strong
                onClick={() => handleOpenPdf("GDPR_cookies")}
                style={{ cursor: "pointer" }}
              >
                Správa Cookies
              </strong>{" "}
              <span
                onClick={() => handleOpenPdf("GDPR_cookies_CZ")}
                style={{ cursor: "pointer" }}
              >
                (verzia CZ)
              </span>
            </li>
          </ul>
          <div className="dui-contact-form-info-bottom">
            <div className="dui-contact-form-support">
              <ul>
                <li>
                  <strong>Podpora zákazníkov</strong>
                </li>
                <li>
                  <a href="mailto:info@doktorui.com">info@doktorui.com</a>
                </li>
                <li>+421 918 191 061</li>
              </ul>
            </div>
            <div className="dui-contact-form-company">
              <ul>
                <li>
                  <strong>IDEOLIC s.r.o.</strong>
                </li>
                <li>Príkop 843/4, Zábrdovice</li>
                <li>602 00 Brno</li>
                <li>Česká Republika</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
